// The outline of the hovered cell (R1875, ui-map-interface.md §8 "The
// peek"): six dashed segments of the cell's hex in the text ink, as faint
// as the peek's leader, while a peek of a map hover stands (a hull's peek
// outlines no cell). It took over the heat cell outline of R1825,
// which drew in the heatmap's slot and only while a mode painted; this
// layer draws it for every peek of a map hover, so it has a slot of its
// own, right after the heatmap: over the tint, under the hulls.
//
// One cell, one batch of six segments (§10.1). The dash is the segment
// shader's, in screen px, so a zoom repacks nothing. The segments pack only
// on a change of the cell (§10.3), and the layer is invisible while no cell
// stands, so the engine neither advances nor draws it.

import type { Engine } from '../engine';
import { F_SCREEN, type DrawCtx, type FrameInfo, type Layer } from '../layer';
import type { SegmentSink } from './batches';
import { PEEK_CELL } from '../../gen/presentation';
import { cellCenter } from '../../world';
import { hexOutline } from './shared';

export class HoverCellLayer implements Layer {
  readonly name = 'hovercell';

  private readonly sink: SegmentSink;
  private cellV = -1;
  private shown = true;

  constructor(engine: Pick<Engine, 'newSegments'>) {
    this.sink = engine.newSegments(6);
  }

  /** The layer draws while a cell stands and nobody hid it. */
  get visible(): boolean { return this.shown && this.cellV >= 0; }
  setVisible(v: boolean): void { this.shown = v; }

  /** The cell the outline stands on, −1 for none. */
  get cell(): number { return this.cellV; }

  /**
   * Outlines one cell, or none with −1. The tooltip calls it on every
   * refresh of the hover; a repeat packs nothing (§10.3).
   */
  setCell(cell: number): void {
    if (cell === this.cellV) return;
    this.cellV = cell;
    const s = this.sink;
    s.begin();
    if (cell < 0) return;
    const [cx, cy] = cellCenter(cell);
    const { width, alpha, color, dashPx, gapPx } = PEEK_CELL;
    hexOutline(s, cx, cy, width, color, alpha, F_SCREEN, dashPx, gapPx);
  }

  update(_frame: FrameInfo): void { /* the outline changes only with the cell */ }

  draw(ctx: DrawCtx): void {
    this.sink.flush(ctx.cam);
  }
}
